import { Div } from "atomize"
import { useContext, useState } from "react"
import { animate, motion, useMotionValue, useSpring, useTransform } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPen, faRotateLeft, faTrash } from "@fortawesome/free-solid-svg-icons"
import { faClone } from "@fortawesome/free-regular-svg-icons"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { isCancelledLesson } from "../utils/cancellation.utils"
import { haptic } from "../utils/haptics.utils"

// Quanto va trascinata una lezione perché il gesto valga
const SWIPE_THRESHOLD = 150

// Le due cose che si fanno a una lezione senza aprirla, con un dito: verso
// destra si duplica, verso sinistra si modifica. Prima verso destra si
// eliminava, ma una lezione si rifà molto più spesso di quanto si cancelli:
// eliminare resta tra i bottoni della lezione aperta, dove non scappa.
//
// Una lezione annullata non si duplica né si modifica: verso sinistra si
// ripristina, e verso destra si elimina, se non doveva esserci.
//
// Lo stesso gesto nell'elenco del giorno e nell'agenda della settimana: chi
// lo impara in un posto lo trova anche nell'altro. `children` riceve se la
// lezione si sta trascinando, per arrotondarla tutta mentre si stacca.
function SwipeActions({ lessonData, canEdit, children }) {
	const Modal = useContext(modalContext)
	const [isDragging, setIsDragging] = useState(false)
	const isCancelled = isCancelledLesson(lessonData)

	const x = useMotionValue(0)
	const dragX = useMotionValue(0)

	const rightActionWidth = useTransform(dragX, x => Math.max(0, x))
	const leftActionWidth = useTransform(dragX, x => Math.max(0, -x))

	const rightIconOpacity = useSpring(
		useTransform(rightActionWidth, w => (w > 30 ? 1 : 0)),
		{ stiffness: 400, damping: 25 },
	)
	const leftIconOpacity = useSpring(
		useTransform(leftActionWidth, w => (w > 30 ? 1 : 0)),
		{ stiffness: 400, damping: 25 },
	)

	const right = isCancelled
		? { icon: faTrash, color: theme.colors.red, bg: theme.colors.red_light, modal: "deleteLesson" }
		: {
				icon: faClone,
				color: theme.colors.green,
				bg: theme.colors.green_light,
				modal: "duplicateLesson",
		  }
	const left = isCancelled
		? { icon: faRotateLeft, modal: "restoreLesson" }
		: { icon: faPen, modal: "editLesson" }

	const handleDragEnd = (_, info) => {
		setIsDragging(false)
		const deltaX = info.offset.x
		animate(x, 0, { type: "spring", stiffness: 400, damping: 30 })
		if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
			haptic("tick")
			Modal.open(deltaX < 0 ? left.modal : right.modal, lessonData)
		}
	}

	const actionStyle = {
		height: "100%",
		borderRadius: "20rem",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	}

	return (
		<Div pos="relative">
			<Div pos="absolute" d="flex" align="center" left="0" h="100%">
				<motion.div style={{ ...actionStyle, width: rightActionWidth, background: right.bg }}>
					<motion.div style={{ opacity: rightIconOpacity }}>
						<FontAwesomeIcon icon={right.icon} size="xl" color={right.color} />
					</motion.div>
				</motion.div>
			</Div>
			<Div pos="absolute" d="flex" align="center" right="0" h="100%">
				<motion.div
					style={{ ...actionStyle, width: leftActionWidth, background: theme.colors.blue_light }}>
					<motion.div style={{ opacity: leftIconOpacity }}>
						<FontAwesomeIcon icon={left.icon} size="xl" color={theme.colors.blue} />
					</motion.div>
				</motion.div>
			</Div>
			<Div pos="relative">
				<motion.div
					style={{ x: dragX }}
					drag={canEdit && "x"}
					dragConstraints={{ left: 0, right: 0 }}
					onDragStart={() => setIsDragging(true)}
					onDragEnd={handleDragEnd}>
					{children(isDragging)}
				</motion.div>
			</Div>
		</Div>
	)
}

export default SwipeActions
